import {
    Box, FormControl, Grid, Card, CardContent, Typography, Chip, Avatar, TextField, MenuItem
} from "@mui/material";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import { useState, useEffect, useMemo } from "react";
import {
    GetTargetAchievementAction,
    GetWorkLoadAction,
    GetMandateReportsAction,
    GetQualityofResumesAction,
    GetReportRecruiterAction
} from "../../../../../store/ATS/Report/Action";
import { useDispatch, useSelector } from "react-redux";
import { getUserData } from "../../../Base/helper/baseFunctions";
import Chart from "react-apexcharts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// ─── Brand & palette ──────────────────────────────────────────────────────────
const BRAND = "#8B44FF";
const PALETTE = ["#8B44FF", "#4E79A7", "#10B981", "#F28E2B", "#E15759", "#76B7B2", "#59A14F", "#EDC948"];
const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}-${("0" + d.getDate()).slice(-2)}`;
};
const fyStart = () => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${y}-04-01`;
};
const pct = (n, d) => d > 0 ? `${Math.round((n / d) * 100)}%` : "0%";

// ─── Summary Metric Card ──────────────────────────────────────────────────────
const MetricCard = ({ icon, value, label, sub, color, trend }) => (
    <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <Avatar sx={{ bgcolor: `${color}18`, color, width: 38, height: 38 }}>{icon}</Avatar>
                {trend != null && (
                    <Chip label={trend >= 0 ? `+${trend}%` : `${trend}%`} size="small"
                        sx={{ bgcolor: trend >= 0 ? "#ECFDF5" : "#FEF2F2", color: trend >= 0 ? "#059669" : "#EF4444", fontWeight: 700, fontSize: "0.65rem" }}
                    />
                )}
            </Box>
            <Typography fontWeight={800} fontSize="1.5rem" lineHeight={1} sx={{ mt: 1 }}>{value}</Typography>
            <Typography fontSize="0.75rem" color="text.secondary" sx={{ mt: 0.3 }}>{label}</Typography>
            {sub && <Typography fontSize="0.68rem" color={color} fontWeight={600} sx={{ mt: 0.3 }}>{sub}</Typography>}
        </CardContent>
    </Card>
);

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ title, sub }) => (
    <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 4, height: 20, borderRadius: 2, bgcolor: BRAND }} />
        <Box>
            <Typography variant="subtitle1" fontWeight={800} color="#1E293B">{title}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
    </Box>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const AnalyticsReportNew = () => {
    const dispatch = useDispatch();
    const user = getUserData();
    const [userData, setUserData] = useState({ load: true, values: [] });
    const [filters, setFilters] = useState({
        from_date: fyStart(),
        to_date: todayStr(),
        primary_recruiter_filter: "",
    });

    // Redux state from all relevant reports
    const { atsTargetAchievement } = useSelector(s => s.AtsReducer?.AtsReportReducer);
    const { atsWorkLoadData }       = useSelector(s => s.AtsReducer?.AtsReportReducer);
    const { atsMandateReport }      = useSelector(s => s.AtsReducer?.AtsReportReducer);
    const { atsQualityofResume }    = useSelector(s => s.AtsReducer?.AtsReportReducer);
    const { atsRecruiterData }      = useSelector(s => s.AtsReducer?.AtsReportReducer);

    // ── Bootstrap dropdowns ───────────────────────────────────────────────────
    useEffect(() => {
        dispatch(GetReportRecruiterAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (atsRecruiterData?.length > 0) setUserData({ load: false, values: atsRecruiterData });
    }, [atsRecruiterData]);

    // ── Fetch all data ────────────────────────────────────────────────────────
    useEffect(() => {
        const recruiter = filters.primary_recruiter_filter
            ? `{${filters.primary_recruiter_filter.ats_users_id}}`
            : null;

        dispatch(GetTargetAchievementAction({ from_date: filters.from_date, to_date: filters.to_date, primary_recruiter: recruiter }));
        dispatch(GetWorkLoadAction({ from_date: null, to_date: null, primary_recruiter: recruiter }));
        dispatch(GetQualityofResumesAction({ from_date: filters.from_date, to_date: filters.to_date, primary_recruiter: recruiter, secondary_recruiter: null }));
        dispatch(GetMandateReportsAction({ recruiter_id: recruiter, client_id: null }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.from_date, filters.to_date, filters.primary_recruiter_filter]);

    const handleFilter = (event, value) => {
        if (event === "primary_recruiter_filter") setFilters(f => ({ ...f, primary_recruiter_filter: value || "" }));
        if (["from_date", "to_date"].includes(event?.target?.name)) setFilters(f => ({ ...f, [event.target.name]: event.target.value }));
    };

    // ──────────────────────────────────────────────────────────────────────────
    // 1. TARGET ACHIEVEMENT aggregate
    const activity = atsTargetAchievement || [];
    const totalProposed   = activity?.reduce((s, r) => s + (parseInt(r.shortlisted) || 0), 0) || 0;
    const totalInterviewed = activity?.reduce((s, r) => s + (parseInt(r.interviews_scheduled) || 0), 0) || 0;
    const totalSelected   = activity?.reduce((s, r) => s + (parseInt(r.selected) || 0), 0) || 0;
    const totalRejected   = activity?.reduce((s, r) => s + (parseInt(r.rejected) || 0), 0) || 0;
    const selectionRate   = totalProposed > 0 ? Math.round((totalSelected / totalProposed) * 100) : 0;

    // Recruiter productivity map
    const recruiterActivity = useMemo(() => {
        const map = {};
        activity?.forEach(r => {
            const n = r.recruiter_name || "Unknown";
            if (!map[n]) map[n] = { proposed: 0, interviewed: 0, selected: 0, rejected: 0 };
            map[n].proposed   += parseInt(r.shortlisted) || 0;
            map[n].interviewed += parseInt(r.interviews_scheduled) || 0;
            map[n].selected   += parseInt(r.selected) || 0;
            map[n].rejected   += parseInt(r.rejected) || 0;
        });
        return map;
    }, [activity]);

    // Monthly trend from activity
    const monthMap = useMemo(() => {
        const map = {};
        activity?.forEach(r => {
            if (!r.modified_date) return;
            const d = new Date(r.modified_date);
            if (isNaN(d)) return;
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,"0")}`;
            if (!map[key]) map[key] = { proposed: 0, selected: 0 };
            map[key].proposed += parseInt(r.shortlisted) || 0;
            map[key].selected += parseInt(r.selected) || 0;
        });
        return map;
    }, [activity]);
    const monthKeys = Object.keys(monthMap)?.sort();

    // 2. WORKLOAD aggregate
    const workload = atsWorkLoadData || [];
    const wlRecruiterMap = {};
    workload?.forEach(r => {
        const n = r.recruiter_name || "Unknown";
        if (!wlRecruiterMap[n]) wlRecruiterMap[n] = { jobs: 0, positions: 0 };
        wlRecruiterMap[n].jobs += parseInt(r.no_of_jobs) || 0;
        wlRecruiterMap[n].positions += parseInt(r.no_of_positions) || 0;
    });
    const totalRecruiters = Object.keys(wlRecruiterMap).length;
    const totalOpenJDs = Object.values(wlRecruiterMap)?.reduce((s, v) => s + v.jobs, 0) || 0;
    const avgJDs = totalRecruiters > 0 ? Math.round(totalOpenJDs / totalRecruiters) : 0;

    // 3. MANDATE aggregate
    const mandates = atsMandateReport || [];
    const totalMandates = mandates?.length || 0;
    const activeMandates = mandates?.filter(m => (m.status || "").toLowerCase().includes("open") || (m.status || "").toLowerCase().includes("active")).length || 0;

    // 4. QUALITY aggregate
    const quality = atsQualityofResume || [];
    const totalResumes = quality?.reduce((s, r) => s + (parseInt(r.total_resumes) || parseInt(r.shortlisted) || 0), 0) || 0;

    // ──────────────────────────────────────────────────────────────────────────
    // CHARTS
    // (A) Monthly trend area
    const trendChart = {
        options: {
            chart: { type: "area", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: [BRAND, "#10B981"],
            fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 95] } },
            stroke: { curve: "smooth", width: 2.5 },
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            markers: { size: 4, strokeWidth: 0 },
            xaxis: { categories: monthKeys.length ? monthKeys : ["No Data"], labels: { style: { fontSize: "10px", colors: "#94A3B8" } } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { position: "top", fontSize: "12px", markers: { radius: 3 } },
        },
        series: [
            { name: "Proposed", data: monthKeys.map(k => monthMap[k].proposed) },
            { name: "Selected", data: monthKeys.map(k => monthMap[k].selected) },
        ],
    };

    // (B) Recruiter performance stacked bar
    const topRecruiters = Object.entries(recruiterActivity).sort(([, a], [, b]) => b.proposed - a.proposed).slice(0, 10);
    const perfBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false }, stacked: false },
            colors: [BRAND, "#4E79A7", "#10B981"],
            plotOptions: { bar: { columnWidth: "60%", borderRadius: 4 } },
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: { categories: topRecruiters.map(([n]) => n.split(" ")[0]), labels: { style: { fontSize: "11px", fontWeight: 600 }, rotate: -30 } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { position: "top", fontSize: "12px", markers: { radius: 3 } },
        },
        series: [
            { name: "Proposed", data: topRecruiters.map(([, v]) => v.proposed) },
            { name: "Interviewed", data: topRecruiters.map(([, v]) => v.interviewed) },
            { name: "Selected", data: topRecruiters.map(([, v]) => v.selected) },
        ],
    };

    // (C) Funnel donut
    const funnelDonut = {
        options: {
            chart: { type: "donut", fontFamily: "Nunito Sans" },
            labels: ["Proposed", "Interviewed", "Selected", "Rejected"],
            colors: [BRAND, "#4E79A7", "#10B981", "#EF4444"],
            dataLabels: { formatter: (_, opts) => opts.w.globals.series[opts.seriesIndex] },
            stroke: { width: 0 },
            legend: { position: "bottom", fontSize: "11px", markers: { radius: 3 } },
            plotOptions: { pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Proposed", fontSize: "11px", color: "#94A3B8" } } } } },
        },
        series: [totalProposed || 1, totalInterviewed || 0, totalSelected || 0, totalRejected || 0],
    };

    // (D) Workload horizontal bar (top 8)
    const wlTop8 = Object.entries(wlRecruiterMap).sort(([, a], [, b]) => b.jobs - a.jobs).slice(0, 8);
    const wlBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: wlTop8.map(([, v]) => v.jobs > avgJDs ? BRAND : "#E15759"),
            plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true, barHeight: "55%" } },
            dataLabels: { enabled: true, style: { fontSize: "11px", fontWeight: 600 } },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            yaxis: { labels: { style: { fontSize: "11px", fontWeight: 500 } } },
            legend: { show: false },
            annotations: avgJDs > 0 ? { xaxis: [{ x: avgJDs, borderColor: "#F59E0B", strokeDashArray: 4, label: { text: `Avg: ${avgJDs}`, style: { fontSize: "10px", color: "#D97706", background: "#FEF3C7" } } }] } : {},
        },
        series: [{ data: wlTop8.map(([n, v]) => ({ x: n.split(" ")[0], y: v.jobs })) }],
    };

    // (E) Mandate status donut
    const mandateStatus = {};
    mandates.forEach(m => { const k = m.status || "Unknown"; mandateStatus[k] = (mandateStatus[k] || 0) + 1; });
    const mLabels = Object.keys(mandateStatus);
    const mandateDonut = {
        options: {
            chart: { type: "donut", fontFamily: "Nunito Sans" },
            labels: mLabels.length ? mLabels : ["No Data"],
            colors: PALETTE.slice(0, mLabels.length || 1),
            dataLabels: { enabled: false },
            stroke: { width: 0 },
            legend: { position: "bottom", fontSize: "11px", markers: { radius: 3 } },
            plotOptions: { pie: { donut: { size: "70%", labels: { show: true, total: { show: true, label: "Mandates", fontSize: "12px", color: "#94A3B8" } } } } },
        },
        series: mLabels.length ? mLabels.map(k => mandateStatus[k]) : [1],
    };

    // Top performers by selected
    const topPerformers = Object.entries(recruiterActivity)
        .sort(([, a], [, b]) => b.selected - a.selected)
        .slice(0, 6);

    return (
        <>
            <Box sx={{ mt: 1 }}>
                {/* ── Filters ──────────────────────────────────────────── */}
                <Grid container columnSpacing={1} mb={1.5} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <SelectableSearch
                            isOptionEqualToValue={(o, v) => o?.ats_users_id === v?.ats_users_id}
                            label="Recruiter (All)" value={filters.primary_recruiter_filter}
                            loading={() => setUserData({ load: false, values: atsRecruiterData })}
                            fieldLabel="first_name" required={false}
                            variant="outlined" name="primary_recruiter_filter"
                            onChangeEvent={handleFilter} data={userData} size="small"
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2.5}>
                        <FormControl fullWidth>
                            <CommonDatePicker
                                size="small" name="from_date" label="From Date" pickerType="date"
                                onKeyDown={e => e.preventDefault()}
                                pickerFormat="dd/MM/yyyy" DefaultDTvalue={filters.from_date} maxDate={new Date()}
                                handleChange={v => handleFilter({ target: { name: "from_date", value: v ? `${v.getFullYear()}-${("0" + (v.getMonth() + 1)).slice(-2)}-${("0" + v.getDate()).slice(-2)}` : "" } })}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2.5}>
                        <FormControl fullWidth>
                            <CommonDatePicker
                                size="small" name="to_date" label="To Date" pickerType="date"
                                onKeyDown={e => e.preventDefault()}
                                pickerFormat="dd/MM/yyyy" DefaultDTvalue={filters.to_date} maxDate={new Date()} minDate={filters.from_date || null}
                                handleChange={v => handleFilter({ target: { name: "to_date", value: v ? `${v.getFullYear()}-${("0" + (v.getMonth() + 1)).slice(-2)}-${("0" + v.getDate()).slice(-2)}` : "" } })}
                            />
                        </FormControl>
                    </Grid>
                </Grid>

                {/* ══ SECTION 1: High-Level Scorecard ══════════════════════ */}
                <SectionHeader title="Performance Scorecard" sub="Cross-report aggregated KPIs for selected period" />
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={2}>
                        <MetricCard icon={<GroupsOutlinedIcon sx={{ fontSize: 18 }} />} value={totalProposed} label="Proposed" sub={`${pct(totalSelected, totalProposed)} conversion`} color="#4E79A7" />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <MetricCard icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} value={totalInterviewed} label="Interviewed" sub={`${pct(totalInterviewed, totalProposed)} of proposed`} color={BRAND} />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <MetricCard icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />} value={totalSelected} label="Selected" sub={`${selectionRate}% rate`} color="#10B981" />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <MetricCard icon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />} value={totalMandates} label="Mandates" sub={`${activeMandates} active`} color="#F28E2B" />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <MetricCard icon={<BalanceOutlinedIcon sx={{ fontSize: 18 }} />} value={totalOpenJDs} label="Open JDs" sub={`~${avgJDs} per recruiter`} color="#E15759" />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <MetricCard icon={<StarOutlineIcon sx={{ fontSize: 18 }} />} value={totalRecruiters} label="Recruiters" sub="Active in period" color="#76B7B2" />
                    </Grid>
                </Grid>

                {/* ══ SECTION 2: Recruitment Activity ══════════════════════ */}
                <SectionHeader title="Recruitment Activity Trends" sub="Proposed vs Selected across the period" />
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={8}>
                        <Card>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Monthly Trend — Proposed vs Selected</Typography>
                                <Typography variant="caption" color="text.secondary">Candidate pipeline flow across months</Typography>
                                {monthKeys.length > 0 ? (
                                    <Chart options={trendChart.options} series={trendChart.series} type="area" height={230} />
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                        <Typography fontSize="0.82rem">No activity data for selected period</Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ height: "100%" }}>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Recruitment Funnel</Typography>
                                <Typography variant="caption" color="text.secondary">Stage-wise candidate distribution</Typography>
                                <Chart options={funnelDonut.options} series={funnelDonut.series} type="donut" height={230} />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ══ SECTION 3: Recruiter Performance ═════════════════════ */}
                <SectionHeader title="Recruiter Performance" sub="Individual contribution across all activity metrics" />
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={9}>
                        <Card>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Proposed · Interviewed · Selected</Typography>
                                <Typography variant="caption" color="text.secondary">Top 10 recruiters by activity volume</Typography>
                                {topRecruiters.length > 0 ? (
                                    <Chart options={perfBar.options} series={perfBar.series} type="bar" height={250} />
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 220, color: "#94A3B8" }}>
                                        <Typography fontSize="0.82rem">No data for selected period</Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ height: "100%" }}>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                    <EmojiEventsIcon sx={{ fontSize: 16, mr: 0.5, color: "#FECB03", verticalAlign: "text-bottom" }} />
                                    Top Closers
                                </Typography>
                                {topPerformers.map(([name, stats], i) => (
                                    <Box key={name} sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                                        <Avatar sx={{ width: 28, height: 28, bgcolor: i < 3 ? `${medalColors[i]}20` : "#F1F5F9", color: i < 3 ? medalColors[i] : "#94A3B8", fontSize: "0.65rem", fontWeight: 800 }}>
                                            #{i + 1}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} fontSize="0.78rem" noWrap>{name}</Typography>
                                            <Typography variant="caption" color="text.secondary" fontSize="0.68rem">
                                                {stats.selected} selected · {stats.proposed} proposed
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                                {topPerformers.length === 0 && (
                                    <Typography fontSize="0.78rem" color="text.secondary">No data</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ══ SECTION 4: Pipeline Health ════════════════════════════ */}
                <SectionHeader title="Pipeline Health" sub="Workload distribution and mandate pipeline status" />
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Open JDs by Recruiter</Typography>
                                <Typography variant="caption" color="text.secondary">🟣 above avg · 🔴 below avg workload</Typography>
                                {wlTop8.length > 0 ? (
                                    <Chart options={wlBar.options} series={wlBar.series} type="bar" height={240} />
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 210, color: "#94A3B8" }}>
                                        <Typography fontSize="0.82rem">No workload data</Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ height: "100%" }}>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Mandate Status</Typography>
                                <Typography variant="caption" color="text.secondary">Current pipeline breakdown</Typography>
                                {mLabels.length > 0 ? (
                                    <Chart options={mandateDonut.options} series={mandateDonut.series} type="donut" height={240} />
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 210, color: "#94A3B8" }}>
                                        <Typography fontSize="0.82rem">No mandate data</Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ height: "100%" }}>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Pipeline Summary</Typography>
                                {[
                                    { label: "Open Mandates", value: activeMandates, color: "#10B981", pctVal: totalMandates > 0 ? Math.round(activeMandates / totalMandates * 100) : 0 },
                                    { label: "Conversion Rate", value: `${selectionRate}%`, color: BRAND, pctVal: selectionRate },
                                    { label: "Interview Rate", value: pct(totalInterviewed, totalProposed), color: "#4E79A7", pctVal: totalProposed > 0 ? Math.round(totalInterviewed / totalProposed * 100) : 0 },
                                    { label: "Avg JDs / Recruiter", value: avgJDs, color: "#F28E2B", pctVal: Math.min(avgJDs * 5, 100) },
                                ].map(({ label, value, color, pctVal }) => (
                                    <Box key={label} sx={{ mb: 1.5 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                                            <Typography fontSize="0.75rem" color="text.secondary">{label}</Typography>
                                            <Typography fontSize="0.78rem" fontWeight={700} color={color}>{value}</Typography>
                                        </Box>
                                        <Box sx={{ height: 5, borderRadius: 3, bgcolor: "#F1F5F9", overflow: "hidden" }}>
                                            <Box sx={{ height: "100%", width: `${pctVal}%`, bgcolor: color, borderRadius: 3, transition: "width 0.8s ease" }} />
                                        </Box>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default AnalyticsReportNew;
